// components/crm/dialogs/ConvertToStudentDialog.jsx
import {
  AcademicCapIcon,
  ArrowRightIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  PhotoIcon,
  TrashIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../../lib/api';
import { formatBatchTimings } from '../../../lib/batchTimings';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import CourseSearchSelect from '../../ui/CourseSearchSelect';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const DOCUMENT_TYPES = [
  { value: 'adhaar', label: 'Aadhaar Card' },
  { value: 'photo',  label: 'Passport Photo' },
  { value: 'other',  label: 'Other Documents' },
];

// ── Helpers (defined outside component so they never change identity) ──────────
const isBatchFull = (b) => b.assignedStudents >= b.capacity;

const SectionLabel = ({ icon: Icon, label, color = 'text-emerald-600' }) => (
  <div className="flex items-center gap-2 py-1">
    <div className="h-px flex-1 bg-gray-100" />
    <span className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 ${color}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </span>
    <div className="h-px flex-1 bg-gray-100" />
  </div>
);

const FileUploadSkeleton = () => (
  <div className="flex items-center justify-between p-2.5 border rounded-lg bg-gray-50 animate-pulse">
    <div className="flex items-center space-x-3">
      <div className="h-4 w-4 bg-gray-300 rounded" />
      <div className="space-y-1">
        <div className="h-3 w-24 bg-gray-300 rounded" />
        <div className="h-2 w-32 bg-gray-200 rounded" />
      </div>
    </div>
    <div className="h-7 w-20 bg-gray-300 rounded" />
  </div>
);

const StepIndicator = ({ step }) => (
  <div className="flex items-center gap-2 px-6 py-3 bg-white border-b border-gray-100">
    <div className={`flex items-center gap-1.5 text-xs font-medium ${step === 'convert' ? 'text-emerald-700' : 'text-gray-400'}`}>
      <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold
        ${step === 'convert' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-600'}`}>
        {step === 'enroll' ? <CheckCircleSolid className="h-3.5 w-3.5" /> : '1'}
      </div>
      Student Info
    </div>
    <ArrowRightIcon className="h-3 w-3 text-gray-300 flex-shrink-0" />
    <div className={`flex items-center gap-1.5 text-xs font-medium ${step === 'enroll' ? 'text-emerald-700' : 'text-gray-400'}`}>
      <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold
        ${step === 'enroll' ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
        2
      </div>
      Course Enrollment
    </div>
  </div>
);

// ── BLANK STATE ───────────────────────────────────────────────────────────────
const BLANK_STUDENT = {
  name: '', email: '', phone: '',
  address: { street: '', city: '', state: '', pincode: '' },
  guardian: { name: '', phone: '', email: '', relationship: 'father' },
  admissionSource: 'other', documents: [], status: 'active',
};

const BLANK_ENROLLMENT = {
  courseId: '', batchId: '', paymentPlan: 'full',
  numInstallments: 2, installmentAmounts: [], customTotalAmount: '', discountReason: '',
};

// ══════════════════════════════════════════════════════════════════════════════
export default function ConvertToStudentDialog({ lead, open, onOpenChange, onSuccess }) {
  // ── Step ──────────────────────────────────────────────────────────────────
  const [step,         setStep]         = useState('convert');
  const [newStudentId, setNewStudentId] = useState(null);
  const [showSuccess,  setShowSuccess]  = useState(false);

  // ── Step 1 ────────────────────────────────────────────────────────────────
  const [studentData,    setStudentData]    = useState(BLANK_STUDENT);
  const [loading,        setLoading]        = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState({});

  // ── Step 2 ────────────────────────────────────────────────────────────────
  const [enrollmentData,   setEnrollmentData]   = useState(BLANK_ENROLLMENT);
  const [sdCourses,        setSdCourses]        = useState([]);   // from /student-dashboard/courses
  const [availableBatches, setAvailableBatches] = useState([]);
  const [loadingCourses,   setLoadingCourses]   = useState(false);
  const [loadingBatches,   setLoadingBatches]   = useState(false);
  const [enrolling,        setEnrolling]        = useState(false);

  // ── Derived (step 2) ──────────────────────────────────────────────────────
  const selectedCourse = sdCourses.find(c => c._id === enrollmentData.courseId);
  const selectedBatch  = availableBatches.find(b => b._id === enrollmentData.batchId);

  // ── CORRECT fee logic — mirrors StudentManagement exactly ─────────────────
  const applicableFee = selectedCourse
    ? (selectedBatch?.mode === 'offline'
        ? (selectedCourse.offlineFee ?? selectedCourse.onlineFee ?? 0)
        : (selectedCourse.onlineFee  ?? selectedCourse.offlineFee ?? 0))
    : 0;

  // ── Auto-close timer ref (cleared on unmount) ─────────────────────────────
  const autoCloseTimer = useRef(null);
  useEffect(() => () => clearTimeout(autoCloseTimer.current), []);

  // ── Reset when dialog opens / closes ──────────────────────────────────────
  useEffect(() => {
    if (!open) {
      clearTimeout(autoCloseTimer.current);
      setStep('convert');
      setNewStudentId(null);
      setShowSuccess(false);
      setEnrollmentData(BLANK_ENROLLMENT);
      setAvailableBatches([]);
      setSdCourses([]);
      return;
    }
    if (lead) {
      setStudentData({
        ...BLANK_STUDENT,
        name:            lead.name    || '',
        email:           lead.email   || '',
        phone:           lead.phone   || '',
        admissionSource: lead.source  || 'other',
      });
      const preCourseId = lead.interestCourse?._id || lead.interestCourse || '';
      const preBatchId  = lead.interestBatch?._id  || lead.interestBatch  || '';
      setEnrollmentData({ ...BLANK_ENROLLMENT, courseId: preCourseId, batchId: preBatchId });
    }
  }, [lead, open]);

  // ── Fetch courses from student-dashboard (has correct fee fields) ──────────
  const fetchSdCourses = useCallback(async () => {
    setLoadingCourses(true);
    try {
      const res = await api.get('/student-dashboard/courses');
      setSdCourses(res.data?.data || res.data?.courses || []);
    } catch {
      toast.error('Failed to load courses');
    } finally {
      setLoadingCourses(false);
    }
  }, []);

  // Load courses when we enter step 2
  useEffect(() => {
    if (step === 'enroll' && sdCourses.length === 0) fetchSdCourses();
  }, [step, sdCourses.length, fetchSdCourses]);

  // ── Fetch batches when courseId changes (step 2) ───────────────────────────
  const fetchBatchesForCourse = useCallback(async (courseId) => {
    if (!courseId) { setAvailableBatches([]); return; }
    setLoadingBatches(true);
    try {
      const res = await api.get(`/student-dashboard/courses/${courseId}/batches`);
      if (res.data?.success) {
        setAvailableBatches(res.data.data || []);
      } else {
        setAvailableBatches([]);
        toast.error('Failed to load batches');
      }
    } catch {
      setAvailableBatches([]);
      toast.error('Failed to load batches');
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  // Fetch batches for pre-filled course when step 2 is ready
  useEffect(() => {
    if (step === 'enroll' && enrollmentData.courseId) {
      fetchBatchesForCourse(enrollmentData.courseId);
    }
  }, [step, enrollmentData.courseId, fetchBatchesForCourse]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleFileChange = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File size should not exceed 5MB'); return; }
    setUploadingFiles(p => ({ ...p, [docType]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE_URL}/storage/upload`, {
        method: 'POST', body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setStudentData(p => ({
        ...p,
        documents: [
          ...p.documents.filter(d => d.type !== docType),
          { type: docType, fileName: file.name, fileUrl: data.fileUrl || data.url },
        ],
      }));
      toast.success('File uploaded');
    } catch { toast.error('Failed to upload file'); }
    finally { setUploadingFiles(p => ({ ...p, [docType]: false })); }
  };

  const handleConvertToStudent = async (e) => {
    e.preventDefault();
    if (!lead?._id)                                                   { toast.error('No lead selected'); return; }
    if (!studentData.name || !studentData.email || !studentData.phone){ toast.error('Please fill all required fields'); return; }
    setLoading(true);
    try {
      const res = await api.post(`/leads/${lead._id}/convert`, studentData);
      const sid = res.data?.data?.student?._id;
      setNewStudentId(sid);
      toast.success('Student created! Now set up course enrollment.');
      setStep('enroll');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to convert lead to student'));
    } finally { setLoading(false); }
  };

  const handleCourseChange = async (courseId) => {
    setEnrollmentData({ ...BLANK_ENROLLMENT, courseId });
    setAvailableBatches([]);
    if (courseId) fetchBatchesForCourse(courseId);
  };

  const handleEnrollmentSubmit = async (e) => {
    e.preventDefault();
    if (!newStudentId || !enrollmentData.courseId || !enrollmentData.batchId) {
      toast.error('Please select a course and batch'); return;
    }
    const finalFee = enrollmentData.customTotalAmount ? Number(enrollmentData.customTotalAmount) : applicableFee;
    if (enrollmentData.paymentPlan === 'emi' && enrollmentData.installmentAmounts.length > 0) {
      const sum = enrollmentData.installmentAmounts.reduce((s, a) => s + (Number(a) || 0), 0);
      if (Math.abs(sum - finalFee) > 1) {
        toast.error(`Installment amounts must total ₹${finalFee.toLocaleString()} (current: ₹${sum.toLocaleString()})`);
        return;
      }
    }
    setEnrolling(true);
    try {
      const payload = {
        studentId:   newStudentId,
        batchId:     enrollmentData.batchId,
        paymentPlan: enrollmentData.paymentPlan,
        mode:        selectedBatch?.mode || 'offline',
      };
      if (enrollmentData.paymentPlan === 'emi') {
        payload.numInstallments = enrollmentData.numInstallments;
        if (enrollmentData.installmentAmounts.length === enrollmentData.numInstallments) {
          payload.installmentAmounts = enrollmentData.installmentAmounts.map(Number);
        }
      }
      if (enrollmentData.customTotalAmount) payload.customTotalAmount = Number(enrollmentData.customTotalAmount);
      if (enrollmentData.discountReason)    payload.discountReason    = enrollmentData.discountReason.trim();

      await api.post('/students/enroll', payload);
      // axios throws on non-2xx, so reaching here means success
      toast.success('Student enrolled successfully!');
      setShowSuccess(true);
      autoCloseTimer.current = setTimeout(() => { onSuccess(); }, 2500);
    } catch (err) {
      // Keep dialog open on failure
      toast.error(getApiErrorMessage(err, 'Failed to enroll student'));
    } finally { setEnrolling(false); }
  };

  const getDocIcon = (type) =>
    type === 'photo'
      ? <PhotoIcon className="h-4 w-4 text-emerald-500" />
      : <DocumentTextIcon className="h-4 w-4 text-emerald-500" />;

  // ── Student data setters (stable references via functional update) ─────────
  const setField   = (key) => (e) => setStudentData(p => ({ ...p, [key]: e.target.value }));
  const setAddr    = (key) => (e) => setStudentData(p => ({ ...p, address:  { ...p.address,  [key]: e.target.value } }));
  const setAdmSrc  = (v)        => setStudentData(p => ({ ...p, admissionSource: v }));

  // ── Enrollment data setters ───────────────────────────────────────────────
  const setEField  = (key) => (e) => setEnrollmentData(p => ({ ...p, [key]: e.target.value }));
  const setEVal    = (key) => (v) => setEnrollmentData(p => ({ ...p, [key]: v }));

  // ── Derived (step 2 display) ──────────────────────────────────────────────
  const finalFee = enrollmentData.customTotalAmount ? Number(enrollmentData.customTotalAmount) : applicableFee;
  const n        = enrollmentData.numInstallments;
  const amounts  = enrollmentData.installmentAmounts;
  const amtSum   = amounts.reduce((s, a) => s + (Number(a) || 0), 0);
  const amtOk    = amounts.length === n && Math.abs(amtSum - finalFee) <= 1;
  const discount = applicableFee - finalFee;

  const resetInstallmentsEqual = () => {
    const eq   = Math.floor(finalFee / n);
    const last = Math.round(finalFee - eq * (n - 1));
    setEnrollmentData(p => ({ ...p, installmentAmounts: Array.from({ length: n }, (_, i) => i === n - 1 ? last : eq) }));
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className="!top-0 !bottom-0 !left-20 !right-0 !translate-x-0 !translate-y-0 !max-w-none !w-[calc(100vw-80px)] !h-screen !rounded-none p-0 gap-0 flex flex-col overflow-y-auto">

        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <AcademicCapIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-white leading-tight">Convert Lead to Student</h2>
                <p className="text-xs text-white/70 mt-0.5">
                  {lead?.name} · {step === 'convert' ? 'Fill in student details' : 'Set up course enrollment'}
                </p>
              </div>
            </div>
            <Button size="sm" variant="secondary"
              onClick={() => onOpenChange(false)}
              className="h-8 px-3 text-xs bg-white/20 hover:bg-white/30 text-white border-0 flex-shrink-0">
              Close
            </Button>
          </div>
        </div>

        {/* Step Indicator */}
        {!showSuccess && <StepIndicator step={step} />}

        {/* ── SUCCESS SCREEN ───────────────────────────────────────────────── */}
        {showSuccess && (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 py-16 text-center">
            <div className="h-24 w-24 rounded-full bg-emerald-100 flex items-center justify-center shadow-lg">
              <CheckCircleSolid className="h-14 w-14 text-emerald-500" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-gray-900">Enrolled Successfully!</h3>
              <p className="text-sm text-gray-500 max-w-xs">
                The student has been enrolled in the batch. This window will close automatically.
              </p>
            </div>
            <div className="w-56 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full" style={{ animation: 'countdown 2.5s linear forwards' }} />
            </div>
            <p className="text-xs text-gray-400">Closing in 2 seconds…</p>
          </div>
        )}

        {/* ── STEP 1: Student Info ─────────────────────────────────────────── */}
        {!showSuccess && step === 'convert' && (
          <form onSubmit={handleConvertToStudent} className="flex flex-col">
            <div className="px-6 py-4 space-y-4">

              <SectionLabel icon={UserIcon} label="Basic Information" />
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Full Name <span className="text-red-500">*</span></Label>
                  <Input className="h-9 text-sm" placeholder="Enter student name"
                    value={studentData.name} onChange={setField('name')} required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Email <span className="text-red-500">*</span></Label>
                  <Input className="h-9 text-sm" type="text" placeholder="student@email.com or email1/email2"
                    value={studentData.email} onChange={setField('email')} required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Phone <span className="text-red-500">*</span></Label>
                  <Input className="h-9 text-sm" type="text" placeholder="9876543210 or 9876543210/8765432109"
                    value={studentData.phone} onChange={setField('phone')} required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Admission Source</Label>
                  <Select value={studentData.admissionSource} onValueChange={setAdmSrc}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone_enquiry">Phone Enquiry</SelectItem>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="walkin">Walk-in</SelectItem>
                      <SelectItem value="reference">Reference</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <SectionLabel icon={UserIcon} label="Address" />
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Street Address</Label>
                  <Input className="h-9 text-sm" placeholder="House no., street name"
                    value={studentData.address.street} onChange={setAddr('street')} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700">City</Label>
                    <Input className="h-9 text-sm" placeholder="City"
                      value={studentData.address.city} onChange={setAddr('city')} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700">State</Label>
                    <Input className="h-9 text-sm" placeholder="State"
                      value={studentData.address.state} onChange={setAddr('state')} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700">Pincode</Label>
                    <Input className="h-9 text-sm" placeholder="Pincode"
                      value={studentData.address.pincode} onChange={setAddr('pincode')} />
                  </div>
                </div>
              </div>

              <SectionLabel icon={DocumentTextIcon} label="Documents" />
              <div className="space-y-2">
                {DOCUMENT_TYPES.map((docType) => {
                  const existing    = studentData.documents.find(d => d.type === docType.value);
                  const isUploading = uploadingFiles[docType.value];
                  if (isUploading) return <FileUploadSkeleton key={docType.value} />;
                  return (
                    <div key={docType.value}
                      className="flex items-center justify-between p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-2">
                        {getDocIcon(docType.value)}
                        <div>
                          <p className="text-xs font-medium text-gray-700">{docType.label}</p>
                          {existing && <p className="text-[11px] text-gray-400 truncate max-w-[160px]">{existing.fileName}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {existing ? (
                          <>
                            <a href={existing.fileUrl} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-emerald-600 hover:underline">View</a>
                            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0"
                              onClick={() => setStudentData(p => ({ ...p, documents: p.documents.filter(d => d.type !== docType.value) }))}>
                              <TrashIcon className="h-3.5 w-3.5 text-red-400" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <input type="file" accept={docType.value === 'photo' ? 'image/*' : '*/*'}
                              onChange={e => handleFileChange(e, docType.value)}
                              className="hidden" id={`csd-file-${docType.value}`} />
                            <label htmlFor={`csd-file-${docType.value}`}
                              className="cursor-pointer px-3 py-1 text-xs border border-emerald-200 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1">
                              <ArrowUpTrayIcon className="h-3 w-3" /> Upload
                            </label>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/80 sticky bottom-0">
              <Button type="button" variant="outline" size="sm" className="text-sm h-9"
                onClick={() => onOpenChange(false)} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={loading}
                className="h-9 px-6 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white gap-2">
                {loading ? (
                  <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>Converting…</>
                ) : (
                  <><ArrowRightIcon className="h-3.5 w-3.5" />Convert &amp; Proceed</>
                )}
              </Button>
            </div>
          </form>
        )}

        {/* ── STEP 2: Enrollment ───────────────────────────────────────────── */}
        {!showSuccess && step === 'enroll' && (
          <form onSubmit={handleEnrollmentSubmit} className="flex flex-col">
            <div className="px-6 py-4 space-y-4">

              {/* Lead preference hint */}
              {(lead?.interestCourse || lead?.preferredMode) && (
                <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-emerald-800">
                    <span className="font-semibold">Pre-filled from lead preferences</span> — you can change any field below.
                    {lead.preferredMode && <span className="ml-1 capitalize">Preferred mode: <strong>{lead.preferredMode}</strong>.</span>}
                  </div>
                </div>
              )}

              <SectionLabel icon={AcademicCapIcon} label="Course Selection" />

              {/* Course dropdown */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Select Course <span className="text-red-500">*</span></Label>
                {loadingCourses ? (
                  <div className="h-9 rounded-md bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-pulse" />
                ) : (
                  <CourseSearchSelect
                    courses={sdCourses}
                    loading={loadingCourses}
                    value={enrollmentData.courseId}
                    onChange={handleCourseChange}
                    placeholder="Choose a course"
                    showFees
                  />
                )}
              </div>

              {/* Batch cards */}
              {enrollmentData.courseId && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-gray-700">Select Batch <span className="text-red-500">*</span></Label>
                  {loadingBatches ? (
                    <div className="space-y-2">
                      {[1, 2].map(i => (
                        <div key={i} className="h-20 rounded-lg bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-pulse" />
                      ))}
                    </div>
                  ) : availableBatches.length === 0 ? (
                    <div className="p-6 text-center border-2 border-dashed border-gray-200 rounded-lg">
                      <UserGroupIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No active batches for this course</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {availableBatches.map(batch => {
                        const full   = isBatchFull(batch);
                        const seats  = batch.capacity - batch.assignedStudents;
                        const sel    = enrollmentData.batchId === batch._id;
                        const timing = formatBatchTimings(batch.schedule);
                        // Per-batch fee
                        const batchFee = selectedCourse
                          ? (batch.mode === 'offline'
                              ? (selectedCourse.offlineFee ?? selectedCourse.onlineFee ?? 0)
                              : (selectedCourse.onlineFee  ?? selectedCourse.offlineFee ?? 0))
                          : 0;
                        return (
                          <div key={batch._id}
                            onClick={() => !full && setEnrollmentData(p => ({ ...p, batchId: batch._id, installmentAmounts: [] }))}
                            className={`p-3.5 border-2 rounded-xl cursor-pointer transition-all ${
                              sel  ? 'border-emerald-500 bg-emerald-50/60' :
                              full ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' :
                                     'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/40'
                            }`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="font-semibold text-sm text-gray-900">{batch.name}</span>
                                  <Badge className={`text-[10px] px-1.5 py-0 capitalize ${
                                    batch.mode === 'online' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                  }`}>{batch.mode}</Badge>
                                  {lead?.preferredMode === batch.mode && (
                                    <Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700">✓ Preferred</Badge>
                                  )}
                                  {full
                                    ? <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700">Full</Badge>
                                    : <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700">{seats} seat{seats !== 1 ? 's' : ''}</Badge>
                                  }
                                  {/* Fee chip per batch */}
                                  {batchFee > 0 && (
                                    <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700">
                                      ₹{batchFee.toLocaleString()}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-3 text-[11px] text-gray-500">
                                  {timing && <span className="flex items-center gap-1"><ClockIcon className="h-3 w-3" />{timing}</span>}
                                  <span className="flex items-center gap-1">
                                    <UserIcon className="h-3 w-3" />{batch.assignedStudents}/{batch.capacity}
                                  </span>
                                  <span>{new Date(batch.startDate).toLocaleDateString()} – {new Date(batch.endDate).toLocaleDateString()}</span>
                                </div>
                                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                                  <div className={`h-1.5 rounded-full ${full ? 'bg-red-400' : sel ? 'bg-emerald-500' : 'bg-gray-400'}`}
                                    style={{ width: `${Math.min(100, (batch.assignedStudents / batch.capacity) * 100)}%` }} />
                                </div>
                              </div>
                              {sel && <CheckCircleSolid className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Payment setup — only when course + batch both selected */}
              {enrollmentData.batchId && selectedCourse && selectedBatch && (
                <div className="space-y-3">
                  <SectionLabel icon={AcademicCapIcon} label="Payment Setup" />

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">
                        Final Fee <span className="text-gray-400">(optional override)</span>
                      </Label>
                      <Input type="number" min="0" className="h-9 text-sm"
                        value={enrollmentData.customTotalAmount}
                        onChange={e => setEnrollmentData(p => ({ ...p, customTotalAmount: e.target.value, installmentAmounts: [] }))}
                        placeholder={`Default: ₹${applicableFee.toLocaleString()}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">
                        Adjustment Reason <span className="text-gray-400">(optional)</span>
                      </Label>
                      <Input className="h-9 text-sm"
                        value={enrollmentData.discountReason}
                        onChange={setEField('discountReason')}
                        placeholder="e.g., scholarship, negotiation" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700">Payment Plan</Label>
                      <Select value={enrollmentData.paymentPlan}
                        onValueChange={v => setEnrollmentData(p => ({ ...p, paymentPlan: v, installmentAmounts: [] }))}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">💳 Full Payment</SelectItem>
                          <SelectItem value="emi">📅 EMI / Installments</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {enrollmentData.paymentPlan === 'emi' && (
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-gray-700">Number of Installments</Label>
                        <Select value={String(n)}
                          onValueChange={v => {
                            const newN = Number(v);
                            const eq   = Math.floor(finalFee / newN);
                            const last = Math.round(finalFee - eq * (newN - 1));
                            setEnrollmentData(p => ({
                              ...p,
                              numInstallments: newN,
                              installmentAmounts: Array.from({ length: newN }, (_, i) => i === newN - 1 ? last : eq),
                            }));
                          }}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[2, 3, 4].map(i => <SelectItem key={i} value={String(i)}>{i} installments</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {enrollmentData.paymentPlan === 'emi' && n > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-gray-600">Installment amounts</p>
                        <button type="button" onClick={resetInstallmentsEqual}
                          className="text-[11px] text-emerald-600 hover:underline">
                          Reset to equal
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {Array.from({ length: n }).map((_, i) => (
                          <div key={i} className="space-y-1">
                            <p className="text-[10px] font-medium text-gray-500 uppercase">Inst. {i + 1}</p>
                            <Input type="number" min="1" className="h-8 text-xs"
                              value={amounts[i] ?? ''}
                              onChange={e => {
                                const upd = [...amounts]; upd[i] = e.target.value;
                                setEnrollmentData(p => ({ ...p, installmentAmounts: upd }));
                              }}
                              placeholder={`₹${Math.floor(finalFee / n).toLocaleString()}`} />
                          </div>
                        ))}
                      </div>
                      <div className={`flex items-center justify-between text-xs px-3 py-2 rounded-lg ${amtOk ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        <span>Total: <strong>₹{amtSum.toLocaleString()}</strong></span>
                        <span>Required: <strong>₹{finalFee.toLocaleString()}</strong></span>
                        {amtOk ? <span className="font-semibold">✓ Matches</span> : <span>Diff: ₹{Math.abs(finalFee - amtSum).toLocaleString()}</span>}
                      </div>
                    </div>
                  )}

                  {/* Enrollment Summary */}
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-1.5 text-sm">
                    <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-widest mb-2">Enrollment Summary</p>
                    {[
                      { label: 'Course',               value: selectedCourse?.name || selectedCourse?.courseName },
                      { label: 'Batch',                value: selectedBatch?.name },
                      { label: `Fee (${selectedBatch.mode})`, value: `₹${applicableFee.toLocaleString()}`, bold: true },
                      ...(discount > 0 ? [{ label: 'Discount', value: `-₹${discount.toLocaleString()}`, cls: 'text-emerald-700' }] : []),
                      ...(discount > 0 ? [{ label: 'Final Fee', value: `₹${finalFee.toLocaleString()}`, bold: true }] : []),
                      { label: 'Plan', value: enrollmentData.paymentPlan === 'emi' ? `EMI · ${n} installments` : 'Full Payment' },
                    ].map(({ label, value, bold, cls }) => (
                      <div key={label} className="flex justify-between">
                        <span className="text-gray-600">{label}:</span>
                        <span className={`${bold ? 'font-semibold' : 'font-medium'} ${cls || 'text-gray-900'}`}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/80 sticky bottom-0">
              <Button type="button" variant="ghost" size="sm" className="text-sm text-gray-500 h-9"
                onClick={onSuccess} disabled={enrolling}>
                Skip for now
              </Button>
              <Button type="submit" size="sm"
                disabled={!enrollmentData.courseId || !enrollmentData.batchId || enrolling}
                className="h-9 px-6 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white gap-2">
                {enrolling ? (
                  <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>Enrolling…</>
                ) : (
                  <><CheckCircleIcon className="h-3.5 w-3.5" />Enroll Student</>
                )}
              </Button>
            </div>
          </form>
        )}

      </DialogContent>
    </Dialog>
  );
}
